import { formatInTimeZone } from 'date-fns-tz';
const acceptableStrings = ['short', 'long']
const messageLengthOfReturnString = 'nameLength must be "long" or "short"';

/**
 * throws an error with message from arg
 * @function throwError
 * @param {string} msg - message for error
 */
const throwError = (msg) => {
  throw new Error(msg);
}

/**
 * returns the first day of the specified month of year
 * @function firstDayOfMonth
 * @param {number} year - year to eval
 * @param {number} month - month of year to eval
 * @returns {number} - first day of month in year
 */
const firstDayOfMonth = (year, month) => {
  return new Date(year, month).getDay();
}

/**
 * returns the last day value of date
 * @function dayOfMonth
 * @param {number} year - year that contains month
 * @param {number} month - month to evaluate
 * @param {number} date - day's number
 * @returns {number} - number value of specified day
 */
const dayOfMonth = (year, month, date) => {
  return new Date(year, month, date).getDay();
}

/**
 * returns the number of days in specified month of year
 * @function getDaysInMonth
 * @param {number} year - year to eval
 * @param {number} month - month of year to eval
 * @returns {number} - number of days in specified month of specified year
 */
const getDaysInMonth = (year, month) => {
  return 32 - new Date(year, month, 32).getDate();
}

/**
 * returns the number of days in the previous month
 * @function getPrevMonthDays
 * @param {number} year - year month is contained in
 * @param {number} month - month to evaluate
 * @returns {number} - number of days in month
 */
const getPrevMonthDays = (year, month) => {
  if (month === 1) {
    year--;
    month = 13;
  }
  return 32 - new Date(year, month - 1, 32).getDate();
}

/**
 * returns the name of the month
 * @function getMonthName
 * @param {number} month - number value for month
 * @param {number} year - year month is in
 * @param {string} nameLength - what size of month name to give
 * @param {number} locale - locale of month
 * @returns {string} - name of month
 */
const getMonthName = (month, year, nameLength = 'long', locale = 'en-US') => {
  const evalDate = new Date(year, month);
  const options = { month: nameLength }
  if (!acceptableStrings.includes(nameLength)) throwError(messageLengthOfReturnString);
  return evalDate.toLocaleDateString(locale, options);
}

/**
 * returns the formatted date Month day, Year
 * @function getFormattedDate
 * @param {string} dateString - date in string format
 * @param {string} nameLength - what size of month name to give
 * @param {number} locale - locale of month
 * @returns {string} - formatted date
 */
const getFormattedDate = (dateString, nameLength = 'long', locale = 'en-US') => {
  if (!dateString) return '';

  let dateStringArr = dateString;
  let tempDateString = dateString;

  dateStringArr = dateString.split('-');
  dateStringArr[1] = dateStringArr[1].padStart(2, '0');
  dateStringArr[2] = dateStringArr[2].padStart(2, '0');
  tempDateString = dateStringArr.join('-');

  const evalDate = new Date(`${tempDateString}T00:00:00`);
  const options = {
    month: nameLength,
    day: 'numeric',
    year: 'numeric',
  }
  if (!acceptableStrings.includes(nameLength)) throwError(messageLengthOfReturnString)
  let niceDate = evalDate.toLocaleString(locale, options);
  return niceDate;
}

/**
 * returns the formatted time hh:mm(am/pm) timezone
 * @function getFormattedTime
 * @param {string} date - date in string format
 * @param {string} eventTzid - timezone id
 * @param {string} formal - format for nice niceTime
 * @returns {string} - formatted time
 */
const getFormattedTime = (date, tzid, format = 'h:mmaa zzz') => {
  if (!date) throwError('No date specified for time');
  let niceTime = formatInTimeZone(date, tzid, format);
  return niceTime;
}

// addNewEvents marks only continuation copies as multi_day, so the first
// day keeps its start time unless the event is all_day or a tba_game.
const getEventDisplayTime = (event) => {
  let displayTime = getFormattedTime(event.local_iso8601, event.tzid);
  if (event.event_type === 'tba_game') {
    displayTime = 'TBA';
  } else if (event.multi_day && event.final_day && !event.ends_date_only) {
    const localEndDate = new Date(event.local_end_iso8601);
    displayTime = 'Ends at ' + getFormattedTime(localEndDate, event.tzid);
  } else if (event.multi_day || event.all_day) {
    displayTime = 'All Day';
  }
  return displayTime;
}

/**
 * returns the name of the month
 * @function getDayName
 * @param {number} month - number value for month
 * @param {number} year - year month is in
 * @param {number} date - date of day of month
 * @param {string} nameLength - what size of month name to give
 * @param {number} locale - locale of month
 * @returns {string} - name of day
 */
const getNamesOfDays = ( nameLength = 'long', locale = 'en-US') => {
  let dayNameArr = [];

  for (let i = 1; i < 8; i++) {
    const evalDate = new Date(2022, 4, i);
    evalDate.setHours(0,0,0,0);
    const options = { weekday: nameLength }
    if (!acceptableStrings.includes(nameLength)) throwError(messageLengthOfReturnString)
    dayNameArr.push(evalDate.toLocaleDateString(locale, options));
  }
  return dayNameArr;
}

/**
 * returns an array of arrays that contain the week info
 * @function createWeekNumbers
 * @param {number} prevMonthsDays - number of days in the previous month
 * @param {number} firstDayOfMonth - number value of first day in month to eval
 * @param {number} daysInMonth - number of days in month to eval
 * @param {number} weeksInMonth - number of weeks in month to eval
 * @param {number} currMonth - month to eval
 * @param {number} currYear - current year for month
 * @returns {Array.<Array.<number|date|boolean>>)}
 */
const createWeekNumbers = (prevMonthsDays, firstDayOfMonth, daysInMonth, weeksInMonth, currMonth, currYear) => {
  let weekArr = []
  let lastMonthDays = prevMonthsDays - firstDayOfMonth + 1;
  let nextMonthDays = 1;
  let daysCount = 1;
  // console.log('prevMonthsDays', prevMonthsDays,
  //             '\nfirstDayOfMonth', firstDayOfMonth,
  //             '\nlastMonthDays', lastMonthDays,
  //             '\ndaysInMonth', daysInMonth,
  //             '\nweeksInMonth', weeksInMonth,
  //             '\ncurrMonth', currMonth,
  //             '\ncurrYear', currYear,
  //             );

  for (let i = 0; i < weeksInMonth; i++) {
    let tempWeekArr = []
    if (firstDayOfMonth === 0 && i === 0) {
      for (let i = 0; i < 7; i++) {
        tempWeekArr.push([true, daysCount, new Date(currYear, currMonth, daysCount)]);
        daysCount++;
      }
      weekArr.push(tempWeekArr);
    } else if (firstDayOfMonth !== 0 && i === 0) {
      for (let i = 0; i < firstDayOfMonth; i++) {
        tempWeekArr.push([false, lastMonthDays, new Date(currYear, currMonth - 1, lastMonthDays)]);
        lastMonthDays++;
      }
      for (let i = firstDayOfMonth; i < 7; i++) {
        tempWeekArr.push([true, daysCount, new Date(currYear, currMonth, daysCount)]);
        daysCount++;
      }
      weekArr.push(tempWeekArr);
    } else {
      for (let i = 0; i < 7; i++) {
        if (daysCount <= daysInMonth) {
          tempWeekArr.push([true, daysCount, new Date(currYear, currMonth, daysCount)]);
          daysCount++;
        } else {
          tempWeekArr.push([false, nextMonthDays, new Date(currYear, currMonth + 1, nextMonthDays)]);
          nextMonthDays++;
        }
      }
      weekArr.push(tempWeekArr);
    }
  }
  return weekArr;
}

const pastAndFutureEventsArr = (EventList, currentDateString) => {
  const newFutureObj = {};
  const newPastEventObj = {};
  const pastDateArr = [];
  for (const dateString in EventList) {
    if (dateString > currentDateString) {
      newFutureObj[dateString] = EventList[dateString];
    } else {
      pastDateArr.push(dateString);
    }
  }
  if (pastDateArr.length > 0) {
    newPastEventObj[pastDateArr[pastDateArr.length - 1]] = EventList[pastDateArr[pastDateArr.length - 1]];
  }
  return [newPastEventObj, newFutureObj];
}

const correctDateFormat = (year, month) => {
  let newMonth = (month + 1).toString().padStart(2,'0')
  return `${year}-${newMonth}-01`
}

const changePageButtonHref = (tempMonth) => {
  const pageButton = document.querySelector('.PageBtnTab-tab:not(.u-current)');
  if (!pageButton) {
    return;
  }
  let year = tempMonth.year;
  let newUrl = new URL(pageButton.href);
  newUrl.searchParams.set('month',correctDateFormat(year,tempMonth.month));
  pageButton.setAttribute('href',newUrl.href);
}

// both dateStrings must be something like "2000-01-01"
const sameMonthStrings = (dateString1, dateString2) => {
  if (!dateString1 || !dateString2) {
    return false;
  }
  if (dateString1.length !== 10 || dateString2.length !== 10) {
    return false;
  }
  const sub1 = dateString1.slice(0, 7);
  const sub2 = dateString2.slice(0, 7);
  return sub1 === sub2;
}

export {
  correctDateFormat,
  changePageButtonHref,
  createWeekNumbers,
  dayOfMonth,
  firstDayOfMonth,
  getDaysInMonth,
  getMonthName,
  getFormattedDate,
  getFormattedTime,
  getEventDisplayTime,
  getNamesOfDays,
  getPrevMonthDays,
  pastAndFutureEventsArr,
  sameMonthStrings,
}
